import assets from "../../../assets/praxis-proethica/assets.json";
import module1Record from "../../../assets/praxis-proethica/module-1.json";
import module2Record from "../../../assets/praxis-proethica/module-2.json";
import module3Record from "../../../assets/praxis-proethica/module-3.json";
import module4Record from "../../../assets/praxis-proethica/module-4.json";
import module5Record from "../../../assets/praxis-proethica/module-5.json";
import module6Record from "../../../assets/praxis-proethica/module-6.json";
import module7Record from "../../../assets/praxis-proethica/module-7.json";
import module8Record from "../../../assets/praxis-proethica/module-8.json";
import modulesCollection from "../../../assets/praxis-proethica/modules-collection.json";
import onboardingFlow from "../../../assets/praxis-proethica/onboarding.json";
import theme from "../../../assets/praxis-proethica/theme.json";
import type { EXAM_STATUS } from "../../types/tag";

const moduleRecordsByRef = {
	"content/modules/module-1.json": module1Record,
	"content/modules/module-2.json": module2Record,
	"content/modules/module-3.json": module3Record,
	"content/modules/module-4.json": module4Record,
	"content/modules/module-5.json": module5Record,
	"content/modules/module-6.json": module6Record,
	"content/modules/module-7.json": module7Record,
	"content/modules/module-8.json": module8Record,
} as const;

type ProethicaModuleCatalogEntry = (typeof modulesCollection.modules)[number];
type ProethicaModule =
	(typeof moduleRecordsByRef)[keyof typeof moduleRecordsByRef];

const getModuleRecord = (
	module: ProethicaModuleCatalogEntry,
): ProethicaModule => {
	const moduleRecord =
		moduleRecordsByRef[module.moduleRef as keyof typeof moduleRecordsByRef];
	if (!moduleRecord) {
		throw new Error(`Missing ProEthica module record for ${module.moduleRef}`);
	}
	return moduleRecord;
};

const getModuleLabel = (module: ProethicaModule) =>
	`Module ${module.moduleNumber}`;

const getThumbnailUrl = (kgNodeId: string) => {
	const moduleAsset = assets.modules[kgNodeId as keyof typeof assets.modules];
	if (!moduleAsset?.thumbnail) {
		return "";
	}
	return `${assets.baseUrl}/${moduleAsset.thumbnail}`;
};

const getThumbnailSource = (kgNodeId: string) => {
	const thumbnailUrl = getThumbnailUrl(kgNodeId);
	return thumbnailUrl ? { uri: thumbnailUrl } : undefined;
};

export const proethicaModuleListMeta = Object.fromEntries(
	modulesCollection.modules.map((catalogEntry) => {
		const module = getModuleRecord(catalogEntry);
		return [
			getModuleLabel(module),
			{
				title: module.title,
				titleWithNumber: `${getModuleLabel(module)}: ${module.title}`,
				thumbnailUrl: getThumbnailUrl(module.kgNodeId),
				thumbnailSource: getThumbnailSource(module.kgNodeId),
				subtitle: module.subtitle,
				status: catalogEntry.defaultStatus as EXAM_STATUS,
				disabled: catalogEntry.disabled,
				durationMinutes: module.estimatedDurationMinutes,
				durationLabel: `${module.estimatedDurationMinutes} min`,
				moduleNumber: module.moduleNumber,
				moduleKind: module.moduleKind,
				moduleKindLabel: module.moduleKindLabel,
				moduleRef: catalogEntry.moduleRef,
			},
		];
	}),
);

export const proethicaOnboardingModules = modulesCollection.modules.map(
	(catalogEntry) => {
		const module = getModuleRecord(catalogEntry);
		return {
			id: module.id,
			moduleNumber: module.moduleNumber,
			moduleRef: catalogEntry.moduleRef,
			title: module.title,
			subtitle: module.subtitle,
			summary: module.summary,
			durationMinutes: module.estimatedDurationMinutes,
			durationLabel: `${module.estimatedDurationMinutes} min`,
			moduleKind: module.moduleKind,
			moduleKindLabel: module.moduleKindLabel,
			activityCount: module.activityCount,
			knowledgeCheckQuestionCount: module.knowledgeCheck.questionCount,
			thumbnailUrl: getThumbnailUrl(module.kgNodeId),
			thumbnailSource: getThumbnailSource(module.kgNodeId),
		};
	},
);

export const proethicaTheme = theme;

export const proethicaOnboardingConfig = {
	...onboardingFlow,
	modules: proethicaOnboardingModules,
	startModuleId:
		moduleRecordsByRef[
			onboardingFlow.startModuleRef as keyof typeof moduleRecordsByRef
		]?.id,
} as const;
